import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HealthReportModal from '../Modal/HealthReportModal';
import styled from 'styled-components';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingMessage = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  font-size: 1.2rem;
  color: #1F2937;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00B5B8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  font-size: 1.2rem;
  color: #DC2626;
  max-width: 500px;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ErrorDetail = styled.div`
  font-size: 0.9rem;
  color: #6B7280;
  margin-top: 1rem;
`;

const PublicHealthbook = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detect mobile view based on screen width
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fetchReport = async () => {
      if (!token) {
        setError('ไม่พบ Token ในลิงก์');
        setLoading(false);
        return;
      }

      try {
        // เรียก public API (ไม่ต้องส่ง Authorization header)
        const apiUrl = process.env.REACT_APP_API_URL ||
          (window.location.hostname === 'localhost'
            ? 'http://localhost:8000'
            : 'https://mfuwellnesscenter-server.dietz.asia');
        const response = await fetch(
          `${apiUrl}/api/healthbook/public?token=${token}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว');
        }

        const result = await response.json();

        // Backend ส่งมาแบบ: { status: "success", message: { result: true, data: {...} } }
        const actualData = result.message || result;

        if (!actualData.result || !actualData.data) {
          throw new Error('ไม่พบข้อมูลสุขภาพ');
        }

        // Log เพื่อ debug
        console.log('📦 Received data:', actualData.data);

        // Backend ส่งมาพร้อม parameters แล้ว ไม่ต้อง transform
        const reportData = actualData.data;

        console.log('✅ Report data with parameters:', reportData);
        setReportData(reportData);
      } catch (err) {
        console.error('Error fetching public health report:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [token]);

  // Listen to window resize to update mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>
          <Spinner />
          <div>กำลังโหลดข้อมูลสุขภาพ...</div>
        </LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>{error}</ErrorTitle>
          <ErrorDetail>
            กรุณาตรวจสอบลิงก์หรือติดต่อผู้ดูแลระบบหากพบปัญหา
          </ErrorDetail>
        </ErrorMessage>
      </Container>
    );
  }

  if (!reportData) {
    console.log('❌ reportData is null/undefined');
    return (
      <Container>
        <ErrorMessage>
          <ErrorIcon>❌</ErrorIcon>
          <ErrorTitle>ไม่พบข้อมูล</ErrorTitle>
        </ErrorMessage>
      </Container>
    );
  }

  console.log('🎯 Rendering HealthReportModal with recordData:', reportData);

  // HealthReportModal คาดหวัง array ไม่ใช่ object เดี่ยว
  // ต้องห่อด้วย array
  const recordDataArray = Array.isArray(reportData) ? reportData : [reportData];

  return (
    <HealthReportModal
      isOpen={true}
      onClose={() => window.close()}
      recordData={recordDataArray}
      disableAutoPrint={true}
      isMobileView={isMobileView}
    />
  );
};

export default PublicHealthbook;
